import React, { useCallback, useState } from 'react';
import type { SerializedMusicVideoCut } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../common/types';
import type { useCutActions } from './doc/useCutActions';
import type { CutAutosave } from './doc/useCutAutosave';
import { useMusicVideoEditor, type RecutTarget } from './MusicVideoEditorContext';
import { PhoneEditor } from './phone/PhoneEditor';
import { PhoneTop } from './phone/PhoneTop';
import { useIsPhoneLayout } from './phone/useIsPhoneLayout';
import { FullScreenPlayer } from './preview/FullScreenPlayer';
import { EditorNotices } from './states/EditorNotices';
import { LeaveDialog } from './states/LeaveDialog';
import { MakeDialog } from './states/MakeDialog';
import { MakingNotice } from './states/MakingNotice';
import { ProDialog } from './states/ProDialog';
import { useMakeVideo } from './states/useMakeVideo';
import { EditorTopBar } from './topbar/EditorTopBar';
import { WideEditor } from './WideEditor';

interface MusicVideoEditorFrameProps {
  cut: SerializedMusicVideoCut;
  autosave: CutAutosave;
  guard: { isBlocked: boolean; proceed: () => void; cancel: () => void };
  cutActions: ReturnType<typeof useCutActions>;
  isDirectorWorking: boolean;
  recutTarget: RecutTarget | null;
  onCloseRecut: () => void;
  onLeave: () => void;
}

/**
 * The frame of the open editor: the top bar, the wide layout (design b1) or the phone layout
 * (design f, below 720 px), the notes, "Watch full screen", the make flow (e2 to e5) and the leave
 * dialog.
 */
const MusicVideoEditorFrameComponent: React.FC<MusicVideoEditorFrameProps> = (props) => {
  const { cut, autosave, guard, cutActions, isDirectorWorking, recutTarget, onCloseRecut, onLeave } = props;
  const { accountId, catalogItemUuid, data, doc } = useMusicVideoEditor();
  const isPhone = useIsPhoneLayout();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isFailedNoteOpen, setIsFailedNoteOpen] = useState(cut.directorState === 'failed');
  const [isLeaving, setIsLeaving] = useState(false);
  const make = useMakeVideo({ accountId, catalogItemUuid, cutUuid: cut.uuid, plan: data.plan, flush: autosave.flush });
  const closeFullScreen = useCallback(() => setIsFullScreen(false), []);
  const saveAndLeave = () => {
    setIsLeaving(true);
    autosave
      .flush()
      .then(() => guard.proceed())
      .catch(console.error)
      .finally(() => setIsLeaving(false));
  };

  const notices = (
    <EditorNotices
      isDirectorFailed={isFailedNoteOpen}
      onDismissFailed={() => setIsFailedNoteOpen(false)}
      isRecutWorking={isDirectorWorking}
      making={
        make.makingAssetUuid === null ? null : (
          <MakingNotice
            accountId={accountId}
            catalogItemUuid={catalogItemUuid}
            assetUuid={make.makingAssetUuid}
            cutNumber={cut.number}
            onDismiss={make.clearMaking}
          />
        )
      }
    />
  );
  const topBar = (
    <EditorTopBar
      saveState={autosave.saveState}
      onBack={onLeave}
      onWatch={() => setIsFullScreen(true)}
      onMake={make.start}
      isMaking={make.isBusy}
      onNewCut={cutActions.newCut}
      onCopyCut={cutActions.copyCut}
      isCutBusy={cutActions.isBusy}
    />
  );

  return (
    <>
      {isPhone ? (
        <PhoneEditor
          top={<PhoneTop saveState={autosave.saveState} onBack={onLeave} onMake={make.start} />}
          notices={notices}
        />
      ) : (
        <WideEditor topBar={topBar} notices={notices} recutTarget={recutTarget} onCloseRecut={onCloseRecut} />
      )}
      {isFullScreen && <FullScreenPlayer onClose={closeFullScreen} />}
      <MakeDialog
        isOpen={make.dialog === 'confirm'}
        data={data}
        doc={doc}
        cutNumber={cut.number}
        isBusy={make.isBusy}
        error={make.error}
        saveError={make.saveError}
        onConfirm={() => {
          make.confirm().catch(console.error);
        }}
        onCancel={make.close}
      />
      <ProDialog isOpen={make.dialog === 'pro'} renderCredits={data.plan.renderCredits} onClose={make.close} />
      <LeaveDialog
        isOpen={guard.isBlocked}
        isBusy={isLeaving}
        isSaveFailed={autosave.saveState === 'failed' || autosave.saveState === 'invalid'}
        onSaveAndLeave={saveAndLeave}
        onStay={guard.cancel}
      />
    </>
  );
};

export const MusicVideoEditorFrame = TypedMemo(MusicVideoEditorFrameComponent);
